/**
 * Config Singleton — Single access point for environment variables.
 *
 * All import.meta.env references are centralized here.
 * The rest of the codebase imports Config.getInstance() instead of reading env vars directly.
 */

class Config {
  private static instance: Config;

  private static readonly API_PATH = '/api/v1';
  private static readonly DEFAULT_API_PORT = '8000';
  private static readonly DEFAULT_WS_PORT = '8001';
  private static readonly LOCALHOST_HOSTNAME = 'localhost';
  private static readonly PUBLIC_HTTP_PROTOCOL = 'https:';
  private static readonly PUBLIC_WS_PROTOCOL = 'wss:';
  private static readonly PRIVATE_HTTP_PROTOCOL = 'http:';
  private static readonly PRIVATE_WS_PROTOCOL = 'ws:';
  private static readonly PUBLIC_API_HOSTNAME = 'api.raiso.io';
  private static readonly PUBLIC_WS_HOSTNAME = 'ws.raiso.io';
  private static readonly PUBLIC_APP_HOSTNAMES = new Set([
    'raiso.io',
    'www.raiso.io',
    'app.raiso.io',
    'admin.raiso.io',
    'api.raiso.io',
    'ws.raiso.io',
  ]);

  public readonly apiUrl: string;
  public readonly wsUrl: string;

  private constructor() {
    this.apiUrl = this.resolveApiUrl();
    this.wsUrl = this.resolveWsUrl(this.apiUrl);
  }

  public static getInstance(): Config {
    if (!Config.instance) {
      Config.instance = new Config();
    }
    return Config.instance;
  }

  private getBrowserLocation(): Location | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return window.location;
  }

  private isPublicHostname(hostname: string): boolean {
    return Config.PUBLIC_APP_HOSTNAMES.has(hostname);
  }

  private isLocalHostname(hostname: string): boolean {
    return hostname === Config.LOCALHOST_HOSTNAME || hostname === '127.0.0.1';
  }

  private resolveApiUrl(): string {
    const envApiUrl = import.meta.env.VITE_API_URL;
    const browserLocation = this.getBrowserLocation();

    if (!envApiUrl) {
      return this.buildDefaultApiUrl();
    }

    if (!browserLocation) {
      return envApiUrl;
    }

    try {
      const parsedEnvApiUrl = new URL(envApiUrl);
      if (this.isLocalHostname(parsedEnvApiUrl.hostname) && !this.isLocalHostname(browserLocation.hostname)) {
        return this.buildDefaultApiUrl();
      }
    } catch {
      return this.buildDefaultApiUrl();
    }

    return envApiUrl;
  }

  private resolveWsUrl(apiUrl: string): string {
    const envWsUrl = import.meta.env.VITE_WS_URL;
    const browserLocation = this.getBrowserLocation();

    if (!envWsUrl) {
      return this.buildDefaultWsUrl(apiUrl);
    }

    if (!browserLocation) {
      return envWsUrl;
    }

    try {
      const parsedEnvWsUrl = new URL(envWsUrl);
      if (this.isLocalHostname(parsedEnvWsUrl.hostname) && !this.isLocalHostname(browserLocation.hostname)) {
        return this.buildDefaultWsUrl(apiUrl);
      }
    } catch {
      return this.buildDefaultWsUrl(apiUrl);
    }

    return envWsUrl;
  }

  private buildDefaultApiUrl(): string {
    const browserLocation = this.getBrowserLocation();
    if (!browserLocation) {
      return `${Config.PRIVATE_HTTP_PROTOCOL}//${Config.LOCALHOST_HOSTNAME}:${Config.DEFAULT_API_PORT}${Config.API_PATH}`;
    }

    if (this.isPublicHostname(browserLocation.hostname)) {
      return `${Config.PUBLIC_HTTP_PROTOCOL}//${Config.PUBLIC_API_HOSTNAME}${Config.API_PATH}`;
    }

    return `${Config.PRIVATE_HTTP_PROTOCOL}//${browserLocation.hostname}:${Config.DEFAULT_API_PORT}${Config.API_PATH}`;
  }

  private buildDefaultWsUrl(apiUrl: string): string {
    try {
      const parsedApiUrl = new URL(apiUrl);
      if (this.isPublicHostname(parsedApiUrl.hostname)) {
        return `${Config.PUBLIC_WS_PROTOCOL}//${Config.PUBLIC_WS_HOSTNAME}`;
      }

      return `${Config.PRIVATE_WS_PROTOCOL}//${parsedApiUrl.hostname}:${Config.DEFAULT_WS_PORT}`;
    } catch {
      return `${Config.PRIVATE_WS_PROTOCOL}//${Config.LOCALHOST_HOSTNAME}:${Config.DEFAULT_WS_PORT}`;
    }
  }
}

export default Config;
